import { ForecastDisseminationStatus } from '@app/api/dissemination/types';

interface TimelineTooltipProps {
  forecast: ForecastDisseminationStatus;
  position: { x: number; y: number };
}

export function TimelineTooltip({ forecast, position }: TimelineTooltipProps) {
  // Helper to check if timestamp has timezone info (Z or offset like +00:00)
  const hasTimezoneInfo = (timeStr: string): boolean => /Z$|[+-]\d{2}:\d{2}$/.test(timeStr);

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    // Only add 'Z' if the timestamp doesn't already have timezone info
    const utcDateString = hasTimezoneInfo(dateString) ? dateString : `${dateString}Z`;
    const date = new Date(utcDateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    });
  };

  const formatExpectedTime = (initTimeString: string, expectedTimeString: string | null) => {
    if (!expectedTimeString) return 'N/A';

    // Only add 'Z' if the timestamp doesn't already have timezone info
    const utcInitString = hasTimezoneInfo(initTimeString) ? initTimeString : `${initTimeString}Z`;
    const initDate = new Date(utcInitString);

    // Parse the expected time (format: "HH:MM:SS")
    const [hours, minutes, seconds] = expectedTimeString.split(':').map(Number);

    // Create the full expected datetime by combining date from init_time with time from expected_dissemination_time
    // Use Date.UTC to create a UTC timestamp, then create Date from that
    let expectedDateTime = new Date(
      Date.UTC(
        initDate.getUTCFullYear(),
        initDate.getUTCMonth(),
        initDate.getUTCDate(),
        hours,
        minutes,
        seconds || 0,
        0,
      ),
    );

    // If the combined datetime is before init_time, add 1 day (expected time is on the next day)
    if (expectedDateTime < initDate) {
      expectedDateTime = new Date(expectedDateTime.getTime() + 24 * 60 * 60 * 1000);
    }

    return expectedDateTime.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    });
  };

  const formatDelay = (delay: number | null) => {
    if (delay === null) return 'N/A';
    const absDelay = Math.abs(delay);
    const sign = delay < 0 ? '-' : '+';
    return `${sign}${absDelay.toFixed(1)} min`;
  };

  const formatTimedelta = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    return `${hours}h`;
  };

  const isInProgress = (forecast: ForecastDisseminationStatus) => {
    return forecast.max_prediction_timedelta < forecast.expected_max_prediction_timedelta;
  };

  const shouldShowDelay = (forecast: ForecastDisseminationStatus) => {
    if (!isInProgress(forecast)) {
      return true; // Always show delay for completed forecasts
    }

    // For in-progress forecasts, only show delay if past expected dissemination time
    const now = new Date();
    const utcInitString = hasTimezoneInfo(forecast.init_time)
      ? forecast.init_time
      : `${forecast.init_time}Z`;
    const initTime = new Date(utcInitString);
    const expectedDisseminationTime = forecast.expected_dissemination_time;

    if (expectedDisseminationTime) {
      const [hours, minutes, seconds] = expectedDisseminationTime.split(':').map(Number);
      let expectedDateTime = new Date(
        Date.UTC(
          initTime.getUTCFullYear(),
          initTime.getUTCMonth(),
          initTime.getUTCDate(),
          hours,
          minutes,
          seconds || 0,
          0,
        ),
      );

      // If the combined datetime is before init_time, add 1 day
      if (expectedDateTime < initTime) {
        expectedDateTime = new Date(expectedDateTime.getTime() + 24 * 60 * 60 * 1000);
      }

      return now > expectedDateTime;
    }

    return false;
  };

  return (
    <div
      className="absolute z-50 pointer-events-none"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -100%) translateY(-8px)',
      }}
    >
      <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-3 text-sm min-w-[280px]">
        <div className="space-y-2">
          <div>
            <span className="font-semibold text-gray-700 dark:text-gray-300">Init Time:</span>
            <div className="text-gray-900 dark:text-gray-100">
              {formatDateTime(forecast.init_time)}
            </div>
          </div>

          {forecast.is_missing ? (
            <div className="text-red-600 dark:text-red-400 font-semibold">Missing Forecast</div>
          ) : (
            <>
              <div>
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  Dissemination Time:
                </span>
                <div className="text-gray-900 dark:text-gray-100">
                  {formatDateTime(forecast.dissemination_time)}
                </div>
              </div>

              <div>
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  Expected Dissemination Time:
                </span>
                <div className="text-gray-900 dark:text-gray-100">
                  {formatExpectedTime(forecast.init_time, forecast.expected_dissemination_time)}
                </div>
              </div>

              {shouldShowDelay(forecast) && (
                <div>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Delay:</span>
                  <div
                    className={`font-medium ${
                      forecast.delay && forecast.delay < 0
                        ? 'text-green-600 dark:text-green-400'
                        : forecast.delay && forecast.delay < 30
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {formatDelay(forecast.delay)}
                  </div>
                </div>
              )}

              <div>
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  Max Lead Time:
                </span>
                <div className="text-gray-900 dark:text-gray-100">
                  {formatTimedelta(forecast.max_prediction_timedelta)} /{' '}
                  {formatTimedelta(forecast.expected_max_prediction_timedelta)}
                  {forecast.max_prediction_timedelta <
                    forecast.expected_max_prediction_timedelta && (
                    <span className="ml-2 text-orange-600 dark:text-orange-400 text-xs">
                      (In Progress)
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
